import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import EditIcon from "@mui/icons-material/edit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axios from "axios";
import { MRT_PaginationState } from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SxProps, Theme } from "@mui/material/styles";
import { HEADER } from "../../../minimals/config-global";
import Page from "../../../minimals/components/page";
import { Contact as OrderEntryContact } from "../../orderEntry/types/Contact";
import { PropertyInfo } from "../../orderEntry/types/Property";
import PdfViewerWithLocation from "../../AIDocumentExtractors/PdfViewerWithLocation";
import { PsaFieldResult, PsaHighlightBox } from "../../AIDocumentExtractors/types";
import ContactInfo from "../ContactInfo";
import NewPropertyInfo from "../NewPropertyInfo";
import {
  BlacklistNameEntity,
  BlacklistNameIndividual,
  BlacklistEntityType,
  BlacklistMigrationReviewResponse,
  BlacklistMigrationReviewRow,
  CompanyType,
  CompanyRecord,
  County,
  PersonRecord,
  PropertyRecord,
  State
} from "../types";

const entityLabels: Record<BlacklistEntityType, string> = {
  [BlacklistEntityType.Person]: "Individual",
  [BlacklistEntityType.Company]: "Entity",
  [BlacklistEntityType.Property]: "Property"
};

const emptyRow = null as BlacklistMigrationReviewRow | null;
const nullMessagePattern = /(^|\s)Message:\s*(?:"null"|'null'|\(null\)|<null>|null)(?=\s|$)/gi;
// Dashboard Main reserves this much space below page content on desktop.
const dashboardDesktopBottomPadding = HEADER.H_DASHBOARD_DESKTOP + 8;
const dashboardStickyTop = HEADER.H_DASHBOARD_DESKTOP_OFFSET + 8;
const dashboardStickyGutter = 16;
const dashboardReviewHeaderHeight = 56;
const dashboardStickyPanelTop = dashboardStickyTop + dashboardReviewHeaderHeight;
const reviewRowIdQueryParam = "id";

const cleanText = (value?: string | null) => {
  let trimmed = value?.trim();
  if (!trimmed) {
    return "";
  }

  if (
    trimmed.length >= 2 &&
    ((trimmed.startsWith("\"") && trimmed.endsWith("\"")) || (trimmed.startsWith("'") && trimmed.endsWith("'")))
  ) {
    trimmed = trimmed.slice(1, -1).trim();
  }

  const lower = trimmed.toLowerCase();
  if (lower === "null" || lower === "(null)" || lower === "<null>") {
    return "";
  }

  const cleaned = trimmed.replace(nullMessagePattern, " ").replace(/\s+/g, " ").trim();
  return cleaned.toLowerCase() === "null" || cleaned.toLowerCase() === "(null)" || cleaned.toLowerCase() === "<null>" ? "" : cleaned;
};

const cleanRow = (row: BlacklistMigrationReviewRow): BlacklistMigrationReviewRow => {
  const cleaned = { ...row } as Record<string, any>;
  Object.entries(cleaned).forEach(([key, value]) => {
    if (key === "rawRowJson") {
      return;
    }

    if (typeof value === "string") {
      const text = cleanText(value);
      cleaned[key] = text || undefined;
    }
  });

  return cleaned as BlacklistMigrationReviewRow;
};

const getBulletinViewerUrl = (extractedFile?: string) => {
  const file = cleanText(extractedFile);
  if (!file) return "";
  return `/api/blacklist/openBulletin?id=${encodeURIComponent(file)}&asDownload=false`;
};

const originalCsvDisplayColumns = [
  "id",
  "FieldName",
  "Information",
  "Underwriter",
  "County",
  "City",
  "State",
  "Message",
  "Notes",
  "BulletinDate",
  "BulletinNum",
  "FieldNamePrevValue",
  "BulletinLocation",
  "BulletinLocation2",
  "BulletinLocation3"
];

const normalizeRawHeader = (value: string) => value.replace(/[^a-z0-9]/gi, "").toLowerCase();
const checklistExcludedRawHeaders = new Set(["id", "fieldnameprevvalue", "bulletinlocation", "bulletinlocation2", "bulletinlocation3"].map(normalizeRawHeader));

const parseRawCsvJson = (row: BlacklistMigrationReviewRow) => {
  try {
    return row.rawRowJson ? JSON.parse(row.rawRowJson) : null;
  } catch {
    return row.rawRowJson || null;
  }
};

const getPrimaryRawCsvEntries = (row: BlacklistMigrationReviewRow) => {
  const parsed = parseRawCsvJson(row);
  if (!parsed) {
    return [];
  }

  if (Array.isArray(parsed)) {
    return originalCsvDisplayColumns.map((label, index) => ({ label, value: parsed[index] })).filter((entry) => hasDisplayValue(entry.value));
  }

  if (parsed && typeof parsed === "object") {
    const valuesByNormalizedHeader = Object.entries(parsed).reduce<Record<string, unknown>>((values, [label, value]) => {
      values[normalizeRawHeader(label)] = value;
      return values;
    }, {});

    return originalCsvDisplayColumns
      .map((label) => ({
        label,
        value: valuesByNormalizedHeader[normalizeRawHeader(label)]
      }))
      .filter((entry) => hasDisplayValue(entry.value));
  }

  return hasDisplayValue(parsed) ? [{ label: "Raw row", value: parsed }] : [];
};

const getChecklistRawCsvEntries = (row: BlacklistMigrationReviewRow) =>
  getPrimaryRawCsvEntries(row).filter((entry) => !checklistExcludedRawHeaders.has(normalizeRawHeader(entry.label)));

const formatRawValue = (value: unknown) => {
  return formatDisplayValue(typeof value === "object" && value !== null ? JSON.stringify(value) : value);
};

const splitStoredValues = (value?: string | null) => {
  const text = cleanText(value);
  return text ? text.split("|") : [""];
};

const joinStoredValues = (values?: string[] | null) => values?.filter((value) => cleanText(value)).join("|") || undefined;

type FieldReviewStatus = "accepted" | "rejected" | "notApplicable";
type PrefilledFieldMap = Record<string, true>;
type FieldReviewMap = Record<string, FieldReviewStatus>;
type RejectedCopyMap = Record<string, string>;
type ReviewFieldHoverProps = { onMouseEnter: () => void; onMouseLeave: () => void; onClick: () => void; onFocus: () => void };

type PrefillDecision = "approved" | "rejected";

type AiPrefillSuggestion = {
  key: string;
  label: string;
  value: string;
  bulletinIndex?: number | null;
  pageNumber?: number | null;
  sourceChunkId?: string | null;
  confidence?: "high" | "medium" | "low" | string;
  reason?: string;
  sourceText?: string;
  highlights?: PsaHighlightBox[];
};

type AiPrefillResponse = {
  suggestions: AiPrefillSuggestion[];
  warnings?: string[];
};

type SharedBulletinRecord = {
  entityType: BlacklistEntityType;
  entityId: number;
  displayName?: string;
  details?: string;
  sharedBulletinIds: number[];
  isPotentialMatch?: boolean;
  matchReasons?: string[];
};

type MigrationBulletinInfo = {
  id: number;
  index: number;
  number?: string;
  date?: string | null;
  url?: string;
};

type MigrationReviewPosition = {
  rowId: number;
  pageIndex: number;
  rowNumber: number;
  count: number;
  isInUnimportedQueue?: boolean;
  row?: BlacklistMigrationReviewRow;
};

const formatDisplayValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return cleanText(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const hasDisplayValue = (value: unknown) => Boolean(formatDisplayValue(value));

const normalizeLookupValue = (value?: string | number | null) => cleanText(value === undefined || value === null ? "" : String(value)).toLowerCase().replace(/[^a-z0-9]/g, "");

const parsePositiveInt = (value?: string | null) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

const parseSuggestedBoolean = (value?: string) => {
  const normalized = normalizeLookupValue(value);
  if (["true", "yes", "y", "1"].includes(normalized)) {
    return true;
  }

  if (["false", "no", "n", "0"].includes(normalized)) {
    return false;
  }

  return undefined;
};

const getReviewFieldIdForSuggestion = (key: string) => {
  const fieldIdsBySuggestionKey: Record<string, string> = {
    companyType: "companyTypeId",
    propertyState: "propertyStateId",
    propertyCounty: "propertyCountyId"
  };

  return fieldIdsBySuggestionKey[key] || key;
};

const matchedTextFieldLabels: Record<string, string> = {
  lookupCode: "Lookup Code",
  taxId: "Tax ID",
  notes: "Notes",
  personTitle: "Title",
  personFirstName: "First Name",
  personMiddleName: "Middle Name",
  personLastName: "Last Name",
  personSuffix: "Suffix",
  personYearAdmittedToBar: "Admitted to Bar",
  companyName: "Company Name",
  propertyAddress: "Address",
  propertyUnit: "Unit/Suite/Apt #",
  propertyCity: "City",
  propertyZip: "Zip Code",
  propertySection: "Section/Borough",
  propertyBlock: "Block",
  propertySubdivision: "Subdivision/Condo/PUD/Addition"
};

const getReviewFieldBaseId = (fieldId?: string | null) => cleanText(fieldId).split(":")[0];

const getMatchedTextFieldLabel = (fieldId: string) => {
  if (fieldId.startsWith("propertyLot")) {
    return "Lot";
  }

  if (fieldId.startsWith("propertyParcelId")) {
    return "Parcel";
  }

  return matchedTextFieldLabels[fieldId];
};

const isReviewFieldMatch = (leftFieldId?: string | null, rightFieldId?: string | null) => {
  const leftBase = getReviewFieldBaseId(leftFieldId);
  const rightBase = getReviewFieldBaseId(rightFieldId);
  return Boolean(leftBase && rightBase && leftBase === rightBase);
};

const inferReviewFieldIdForSourceEntry = (row: BlacklistMigrationReviewRow, label: string) => {
  const normalizedLabel = normalizeLookupValue(label);
  if (normalizedLabel === "fieldname") return "source:fieldname";

  const oldField = normalizeLookupValue([row.confirmedFieldName, row.suspectedFieldName, row.fieldName].filter(Boolean).join(" "));
  const fieldText = normalizedLabel === "information" || normalizedLabel === "informationtouseforimport" ? oldField : normalizedLabel;

  if (fieldText.includes("tax")) return "taxId";
  if (fieldText.includes("lookup")) return "lookupCode";
  if (fieldText.includes("note") || fieldText.includes("message")) return "notes";
  if (fieldText.includes("firstname")) return "personFirstName";
  if (fieldText.includes("middlename")) return "personMiddleName";
  if (fieldText.includes("lastname")) return "personLastName";
  if (fieldText.includes("suffix")) return "personSuffix";
  if (fieldText.includes("attorney")) return "personIsAttorney";
  if (fieldText.includes("notary")) return "personIsNotary";
  if (fieldText.includes("companytype")) return "companyTypeId";
  if (fieldText.includes("company") || fieldText.includes("entity")) return "companyName";
  if (fieldText.includes("address") || fieldText.includes("street")) return "propertyAddress";
  if (fieldText.includes("unit") || fieldText.includes("apt")) return "propertyUnit";
  if (fieldText.includes("city")) return "propertyCity";
  if (fieldText.includes("state")) return "propertyStateId";
  if (fieldText.includes("county")) return "propertyCountyId";
  if (fieldText.includes("zip")) return "propertyZip";
  if (fieldText.includes("section")) return "propertySection";
  if (fieldText.includes("block")) return "propertyBlock";
  if (fieldText.includes("subdivision") || fieldText.includes("condo")) return "propertySubdivision";
  if (fieldText.includes("parcel")) return "propertyParcelId";
  if (fieldText.includes("lot")) return "propertyLot";
  if (fieldText.includes("name")) return row.targetEntityType === BlacklistEntityType.Company ? "companyName" : "personLastName";

  return `source:${normalizedLabel || label}`;
};

const toPdfFieldResult = (suggestion: AiPrefillSuggestion): PsaFieldResult => ({
  key: suggestion.key,
  label: suggestion.label || suggestion.key,
  value: suggestion.value,
  pageNumber: suggestion.pageNumber ?? null,
  sourceChunkId: suggestion.sourceChunkId ?? null,
  sourceText: suggestion.sourceText || null,
  confidence: (suggestion.confidence as "high" | "medium" | "low") || "medium",
  highlights: suggestion.highlights || []
});

const getBlacklistRecordUrl = (record: SharedBulletinRecord) => {
  if (record.entityType === BlacklistEntityType.Property) {
    return `/edit-blacklist-property?entityId=${record.entityId}`;
  }

  return `/edit-blacklist-name?entityId=${record.entityId}&entityType=${record.entityType}`;
};

const buildPrefilledFieldMap = (row: BlacklistMigrationReviewRow): PrefilledFieldMap => {
  const prefilled: PrefilledFieldMap = {};
  const markText = (fieldId: string, value?: string | null) => {
    if (cleanText(value)) {
      prefilled[fieldId] = true;
    }
  };
  const markValue = (fieldId: string, value?: number | BlacklistEntityType | null) => {
    if (value !== undefined && value !== null) {
      prefilled[fieldId] = true;
    }
  };
  const markBoolean = (fieldId: string, value?: boolean | null) => {
    if (value) {
      prefilled[fieldId] = true;
    }
  };

  markValue("targetEntityType", row.targetEntityType);
  markText("notes", row.notes);
  markText("lookupCode", row.lookupCode);
  markText("taxId", row.taxId);

  markText("personTitle", row.personTitle);
  markText("personFirstName", row.personFirstName);
  markText("personMiddleName", row.personMiddleName);
  markText("personLastName", row.personLastName);
  markText("personSuffix", row.personSuffix);
  markBoolean("personIsNotary", row.personIsNotary);
  markBoolean("personIsAttorney", row.personIsAttorney);
  markText("personYearAdmittedToBar", row.personYearAdmittedToBar);

  markText("companyName", row.companyName);
  markValue("companyTypeId", row.companyTypeId);

  markText("propertyAddress", row.propertyAddress);
  markText("propertyUnit", row.propertyUnit);
  markText("propertyCity", row.propertyCity);
  markValue("propertyStateId", row.propertyStateId);
  markText("propertyZip", row.propertyZip);
  markValue("propertyCountyId", row.propertyCountyId);
  markText("propertySection", row.propertySection);
  markText("propertyBlock", row.propertyBlock);
  markText("propertySubdivision", row.propertySubdivision);

  splitStoredValues(row.propertyParcelId).forEach((value, index) => {
    if (cleanText(value)) {
      prefilled[`propertyParcelId:${index}`] = true;
    }
  });

  splitStoredValues(row.propertyLot).forEach((value, index) => {
    if (cleanText(value)) {
      prefilled[`propertyLot:${index}`] = true;
    }
  });

  return prefilled;
};

const getPropertyInfoFromRow = (row?: BlacklistMigrationReviewRow | null): PropertyInfo => ({
  isFreeFormEntry: true,
  id: null,
  entries: null,
  owner: null,
  formattedAddress: null,
  address: row?.propertyAddress || "",
  addressHigh: null,
  addressLow: null,
  city: row?.propertyCity || "",
  state: null,
  zipCode: row?.propertyZip || "",
  block: row?.propertyBlock || "",
  lots: splitStoredValues(row?.propertyLot),
  parcelIds: splitStoredValues(row?.propertyParcelId),
  section: row?.propertySection || "",
  borough: null,
  aptNo: row?.propertyUnit || "",
  condoNumber: row?.propertySubdivision || "",
  currentTaxClass: null,
  buildingClass: null,
  county: null,
  propertyUse: null,
  propertyType: null,
  legalDescription: {
    description: null,
    lookupCode: null
  }
});

const emptyTargetFieldValues: Partial<BlacklistMigrationReviewRow> = {
  targetEntityType: undefined,
  targetRecordId: undefined,
  lookupCode: undefined,
  taxId: undefined,
  notes: undefined,
  personTitle: undefined,
  personFirstName: undefined,
  personMiddleName: undefined,
  personLastName: undefined,
  personSuffix: undefined,
  personIsAttorney: false,
  personIsNotary: false,
  personYearAdmittedToBar: undefined,
  companyName: undefined,
  companyTypeId: undefined,
  propertyAddress: undefined,
  propertyUnit: undefined,
  propertyCity: undefined,
  propertyZip: undefined,
  propertySection: undefined,
  propertyBlock: undefined,
  propertySubdivision: undefined,
  propertyStateId: undefined,
  propertyCountyId: undefined,
  propertyParcelId: undefined,
  propertyLot: undefined
};

const copyTextToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

const SourceCsvChecklist = ({
  row,
  fieldReview,
  prefilledFields,
  disabled,
  hoveredReviewFieldId,
  onCopy,
  onAccept,
  onReject,
  onNotApplicable,
  onClearReview,
  onReviewFieldHover,
  onSelectFieldEvidence
}: {
  row: BlacklistMigrationReviewRow;
  fieldReview: FieldReviewMap;
  prefilledFields: PrefilledFieldMap;
  disabled?: boolean;
  hoveredReviewFieldId: string;
  onCopy: (value: string) => void;
  onAccept: (fieldId: string) => void;
  onReject: (fieldId: string, value: unknown) => void;
  onNotApplicable: (fieldId: string) => void;
  onClearReview: (fieldId: string) => void;
  onReviewFieldHover: (fieldId: string) => void;
  onSelectFieldEvidence: (fieldId: string, value: string) => void;
}) => {
  const entries = getChecklistRawCsvEntries(row);

  if (!entries.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Original CSV</Typography>
        <Typography variant="body2" color="text.secondary">No raw CSV data stored for this row.</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, height: "100%" }}>
      <Stack spacing={1.25}>
        <Stack spacing={1}>
          {entries.map((entry, index) => {
            const formattedValue = formatRawValue(entry.value);
            const fieldId = inferReviewFieldIdForSourceEntry(row, entry.label);
            const status = fieldReview[fieldId];
            const hasReviewActions = !fieldId.startsWith("source:") && Boolean(prefilledFields[fieldId] || status);
            const showAllReviewActions = !status;
            const matchedFieldLabel = getMatchedTextFieldLabel(fieldId);
            const canCrossHighlight = status === "accepted" || (Boolean(prefilledFields[fieldId]) && status !== "rejected" && status !== "notApplicable");
            const isMatchedFieldHovered = canCrossHighlight && isReviewFieldMatch(hoveredReviewFieldId, fieldId);

            return (
              <Paper
                key={`${entry.label}-${index}`}
                variant="outlined"
                onClick={() => onSelectFieldEvidence(fieldId, formattedValue)}
                onMouseEnter={() => canCrossHighlight && matchedFieldLabel && onReviewFieldHover(fieldId)}
                onMouseLeave={() => canCrossHighlight && matchedFieldLabel && onReviewFieldHover("")}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  borderColor: isMatchedFieldHovered ? "info.main" : status === "accepted" ? "success.main" : status === "rejected" ? "error.main" : status === "notApplicable" ? "warning.main" : "divider",
                  bgcolor: status === "accepted" ? "success.lighter" : status === "rejected" ? "error.lighter" : status === "notApplicable" ? "warning.lighter" : "background.paper",
                  boxShadow: isMatchedFieldHovered ? 3 : undefined,
                  "&:hover": { boxShadow: 2 }
                }}
              >
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800, textTransform: "uppercase" }}>
                        {entry.label}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, overflowWrap: "anywhere", lineHeight: 1.25 }}>
                        {formattedValue}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} onClick={(event) => event.stopPropagation()}>
                      {hasReviewActions && (showAllReviewActions || status === "accepted") && (
                        <Tooltip title={status === "accepted" ? "Undo keep" : "Keep this value"}>
                          <IconButton
                            size="small"
                            disableRipple
                            disabled={disabled}
                            onClick={() => status === "accepted" ? onClearReview(fieldId) : onAccept(fieldId)}
                            sx={{ color: "success.main", p: 0.25, borderRadius: 0.5 }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {hasReviewActions && (showAllReviewActions || status === "rejected") && (
                        <Tooltip title={status === "rejected" ? "Undo reject" : "Reject, clear matching field, and copy"}>
                          <IconButton
                            size="small"
                            disableRipple
                            disabled={disabled}
                            onClick={() => status === "rejected" ? onClearReview(fieldId) : onReject(fieldId, formattedValue)}
                            sx={{ color: "error.main", p: 0.25, borderRadius: 0.5 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {hasReviewActions && (showAllReviewActions || status === "notApplicable") && (
                        <Tooltip title={status === "notApplicable" ? "Undo not applicable" : "Not applicable, clear matching field"}>
                          <IconButton
                            size="small"
                            disableRipple
                            disabled={disabled}
                            onClick={() => status === "notApplicable" ? onClearReview(fieldId) : onNotApplicable(fieldId)}
                            sx={{ color: "warning.dark", p: 0.25, borderRadius: 0.5 }}
                          >
                            <DoNotDisturbAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Copy value">
                        <IconButton size="small" disabled={disabled} onClick={() => void onCopy(formattedValue)}>
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  {matchedFieldLabel && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: "fit-content" }}
                    >
                      Matched field: {matchedFieldLabel}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

type BulletinOption = {
  index: number;
  label: string;
  file: string;
  bulletinId?: number;
};

const getBulletinOptions = (row?: BlacklistMigrationReviewRow | null): BulletinOption[] => {
  if (!row) return [];

  return [
    { index: 0, label: "1", file: cleanText(row.extractedFile1), bulletinId: row.matchedBulletinId1 },
    { index: 1, label: "2", file: cleanText(row.extractedFile2), bulletinId: row.matchedBulletinId2 },
    { index: 2, label: "3", file: cleanText(row.extractedFile3), bulletinId: row.matchedBulletinId3 }
  ].filter((option) => option.file);
};

const BulletinMatchStrip = ({
  row,
  disabled,
  onOpen,
  onEdit,
  onUnlink
}: {
  row: BlacklistMigrationReviewRow;
  disabled?: boolean;
  onOpen: (index: number) => void;
  onEdit: (index: number) => void;
  onUnlink: (index: number) => void;
}) => {
  const items = [
    { label: "File 1", file: cleanText(row.extractedFile1), bulletinId: row.matchedBulletinId1, index: 0 },
    { label: "File 2", file: cleanText(row.extractedFile2), bulletinId: row.matchedBulletinId2, index: 1 },
    { label: "File 3", file: cleanText(row.extractedFile3), bulletinId: row.matchedBulletinId3, index: 2 }
  ].filter((item) => item.file);

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No bulletin files were stored for this row.
      </Typography>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1}
      divider={<Divider orientation="vertical" flexItem />}
      sx={{ border: "1px solid", borderColor: "divider", px: 1.5, py: 1.25, borderRadius: 1.25 }}
    >
      {items.map((item) => (
        <Stack key={item.label} spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              {item.label} {item.bulletinId ? `-> ${item.bulletinId}` : "-> no match"}
            </Typography>
            <Stack direction="row" spacing={0.25}>
              <Tooltip title="Edit bulletin date/number">
                <span>
                  <IconButton size="small" disabled={disabled || !item.bulletinId} onClick={() => onEdit(item.index)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Unlink this matched bulletin">
                <span>
                  <IconButton size="small" disabled={disabled || !item.bulletinId} onClick={() => onUnlink(item.index)} sx={{ color: "error.main" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Link component="button" type="button" onClick={() => onOpen(item.index)} underline="hover" noWrap sx={{ textAlign: "left", fontSize: 14 }}>
            {item.file}
          </Link>
        </Stack>
      ))}
    </Stack>
  );
};

const BulletinViewer = ({
  options,
  activeIndex,
  activeField,
  onActiveIndexChange,
  isOpen,
  onClose,
  onPdfLoadStart,
  onPdfLoadComplete,
  onPdfLoadError
}: {
  options: BulletinOption[];
  activeIndex: number;
  activeField?: PsaFieldResult | null;
  onActiveIndexChange: (index: number) => void;
  isOpen: boolean;
  onClose: () => void;
  onPdfLoadStart?: (viewerUrl: string) => void;
  onPdfLoadComplete?: (viewerUrl: string) => void;
  onPdfLoadError?: (viewerUrl: string) => void;
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const activeOption = options.find((option) => option.index === activeIndex) || options[0];
  const viewerUrl = activeOption ? getBulletinViewerUrl(activeOption.file) : "";
  const handlePdfLoadStart = useCallback(() => onPdfLoadStart?.(viewerUrl), [onPdfLoadStart, viewerUrl]);
  const handlePdfLoadComplete = useCallback(() => onPdfLoadComplete?.(viewerUrl), [onPdfLoadComplete, viewerUrl]);
  const handlePdfLoadError = useCallback(() => onPdfLoadError?.(viewerUrl), [onPdfLoadError, viewerUrl]);

  if (!isOpen) {
    return null;
  }

  const content = (
    <Stack spacing={1} sx={{ height: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeOption?.index ?? null}
          onChange={(_, value) => value !== null && onActiveIndexChange(value)}
        >
          {options.map((option) => (
            <ToggleButton key={option.index} value={option.index}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Stack direction="row" spacing={0.5}>
          {viewerUrl && (
            <Tooltip title="Open in new tab">
              <IconButton size="small" component="a" href={viewerUrl} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Full size">
            <IconButton size="small" onClick={() => setIsFullScreen(true)} disabled={!viewerUrl}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close PDF">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {activeOption && !activeOption.bulletinId && (
        <Alert severity="warning">This extracted file does not match an existing bulletin.</Alert>
      )}

      {viewerUrl ? (
        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 620, lg: 0 },
            overflow: "hidden",
            "& > div": { height: "100% !important" }
          }}
        >
          <PdfViewerWithLocation
            pdfUrl={viewerUrl}
            activeField={activeField || null}
            centerActiveHighlight
            onLoadStart={handlePdfLoadStart}
            onLoadComplete={handlePdfLoadComplete}
            onLoadError={handlePdfLoadError}
          />
        </Box>
      ) : (
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 260, border: "1px dashed", borderColor: "divider" }}>
          <Typography color="text.secondary">No bulletin available to preview.</Typography>
        </Stack>
      )}

      <Dialog open={isFullScreen} onClose={() => setIsFullScreen(false)} fullScreen>
        <DialogTitle sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">Bulletin {activeOption?.label}</Typography>
            <IconButton onClick={() => setIsFullScreen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "100%" }}>
          {viewerUrl && <PdfViewerWithLocation pdfUrl={viewerUrl} activeField={activeField || null} centerActiveHighlight />}
        </DialogContent>
      </Dialog>
    </Stack>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        height: { xs: "100%", lg: "auto" },
        minHeight: { xs: 720, lg: 0 },
        flex: { lg: 1 },
        overflow: { lg: "hidden" }
      }}
    >
      {content}
    </Paper>
  );
};

export default function BlacklistMigrationReview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialReviewRowId = parsePositiveInt(searchParams.get(reviewRowIdQueryParam));
  const [rowCount, setRowCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState<BlacklistMigrationReviewRow | null>(emptyRow);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 1
  });
  const [activeBulletinIndex, setActiveBulletinIndex] = useState(0);
  const [isBulletinOpen, setIsBulletinOpen] = useState(true);
  const [states, setStates] = useState<State[]>([]);
  const [companyTypes, setCompanyTypes] = useState<CompanyType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResolvingUrlRow, setIsResolvingUrlRow] = useState(Boolean(initialReviewRowId));
  const [isViewingImportedRow, setIsViewingImportedRow] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [advanceMessage, setAdvanceMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [copyMessage, setCopyMessage] = useState("");
  const [nameError, setNameError] = useState(false);
  const [companyError, setCompanyError] = useState(false);
  const [propertyStateError, setPropertyStateError] = useState(false);
  const [propertyCityError, setPropertyCityError] = useState(false);
  const [propertyAddressError, setPropertyAddressError] = useState(false);
  const [propertySblError, setPropertySblError] = useState(false);
  const [isPrefillModalOpen, setIsPrefillModalOpen] = useState(false);
  const [isPrefillLoading, setIsPrefillLoading] = useState(false);
  const [prefillSuggestions, setPrefillSuggestions] = useState<AiPrefillSuggestion[]>([]);
  const [prefillDecisions, setPrefillDecisions] = useState<Record<string, PrefillDecision>>({});
  const [prefillWarnings, setPrefillWarnings] = useState<string[]>([]);
  const [prefillError, setPrefillError] = useState("");
  const [activePdfField, setActivePdfField] = useState<PsaFieldResult | null>(null);
  const [sharedBulletinRecords, setSharedBulletinRecords] = useState<SharedBulletinRecord[]>([]);
  const [isLoadingSharedRecords, setIsLoadingSharedRecords] = useState(false);
  const [loadedSharedRecordsRowId, setLoadedSharedRecordsRowId] = useState<number | null>(null);
  const [loadedBulletinViewerUrl, setLoadedBulletinViewerUrl] = useState("");
  const [linkingExistingRecordKey, setLinkingExistingRecordKey] = useState("");
  const [editingBulletin, setEditingBulletin] = useState<MigrationBulletinInfo | null>(null);
  const [isBulletinEditOpen, setIsBulletinEditOpen] = useState(false);
  const [isLoadingBulletinEdit, setIsLoadingBulletinEdit] = useState(false);
  const [isSavingBulletinEdit, setIsSavingBulletinEdit] = useState(false);
  const [hoveredReviewFieldId, setHoveredReviewFieldId] = useState("");
  const [prefilledFieldsByRow, setPrefilledFieldsByRow] = useState<Record<number, PrefilledFieldMap>>({});
  const [fieldReviewByRow, setFieldReviewByRow] = useState<Record<number, FieldReviewMap>>({});
  const [rejectedCopiesByRow, setRejectedCopiesByRow] = useState<Record<number, RejectedCopyMap>>({});
  const rowFetchRequestId = useRef(0);
  const isNavigatingByPageControl = useRef(false);
  const sharedRecordsRequestId = useRef(0);
  const aiPrefillRequestId = useRef(0);

  const selectedState = states.find((state) => state.id === selectedRow?.propertyStateId);
  const counties: County[] = selectedState?.counties || [];
  const selectedCounty = counties.find((county) => county.id === selectedRow?.propertyCountyId) || null;
  const selectedCompanyType = companyTypes.find((type) => type.id === selectedRow?.companyTypeId) || null;
  const bulletinOptions = useMemo(() => getBulletinOptions(selectedRow), [selectedRow]);
  const activeBulletinOption = bulletinOptions.find((option) => option.index === activeBulletinIndex) || bulletinOptions[0];
  const activeBulletinViewerUrl = isBulletinOpen && activeBulletinOption ? getBulletinViewerUrl(activeBulletinOption.file) : "";
  const hasMatchedBulletin = bulletinOptions.some((option) => Boolean(option.bulletinId));
  const isSelectedRowImported = Boolean(selectedRow?.imported);
  const hasPreviousRow = pagination.pageIndex > 0;
  const hasNextRow = isSelectedRowImported
    ? rowCount > 0 && pagination.pageIndex < rowCount
    : (pagination.pageIndex + 1) * pagination.pageSize < rowCount;
  const currentRowNumber = rowCount ? Math.min(pagination.pageIndex + 1, rowCount) : 0;
  const selectedRowSummary = [cleanText(selectedRow?.fieldName), cleanText(selectedRow?.information)].filter(Boolean).join(" | ");
  const isSelectedRowChecklistReady = !selectedRow || Boolean(prefilledFieldsByRow[selectedRow.id]);
  const isSelectedRowSharedRecordsReady = !selectedRow || loadedSharedRecordsRowId === selectedRow.id;
  const isSelectedRowPdfReady = !selectedRow || !activeBulletinViewerUrl || loadedBulletinViewerUrl === activeBulletinViewerUrl;
  const hasLinkedTargetRecord = Boolean(selectedRow?.targetRecordId && selectedRow?.targetEntityType);
  const isEditingExistingRecord = Boolean(!isSelectedRowImported && hasLinkedTargetRecord);
  const isReviewRowLoading = Boolean(
    isResolvingUrlRow ||
    isLoading ||
    (selectedRow && (!isSelectedRowChecklistReady || !isSelectedRowSharedRecordsReady || !isSelectedRowPdfReady))
  );

  const fetchRows = async () => {
    const requestId = rowFetchRequestId.current + 1;
    rowFetchRequestId.current = requestId;
    setIsViewingImportedRow(false);
    setIsLoading(true);
    setError("");
    setSelectedRow(null);
    try {
      const { data } = await axios.post<BlacklistMigrationReviewResponse>("/api/blacklist/getMigrationReviewRows", {
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageIndex,
        imported: false
      });
      if (rowFetchRequestId.current !== requestId) {
        return;
      }

      const cleanedRows = data.data.map(cleanRow);
      setRowCount(data.count);
      if (data.count > 0 && pagination.pageIndex >= data.count) {
        setSelectedRow(null);
        isNavigatingByPageControl.current = false;
        setPagination((current) => ({ ...current, pageIndex: data.count - 1 }));
        return;
      }

      isNavigatingByPageControl.current = false;
      setSelectedRow(cleanedRows[0] || null);
    } catch (err: any) {
      if (rowFetchRequestId.current !== requestId) {
        return;
      }

      isNavigatingByPageControl.current = false;
      setError(err?.response?.data || "Could not load migration review rows.");
    } finally {
      if (rowFetchRequestId.current !== requestId) {
        return;
      }

      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isResolvingUrlRow || isViewingImportedRow) {
      return;
    }

    fetchRows();
  }, [isResolvingUrlRow, isViewingImportedRow, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    const rowId = parsePositiveInt(searchParams.get(reviewRowIdQueryParam));
    if (isNavigatingByPageControl.current) {
      setIsResolvingUrlRow(false);
      return;
    }

    if (!rowId || selectedRow?.id === rowId) {
      setIsResolvingUrlRow(false);
      return;
    }

    let isCurrent = true;
    setIsResolvingUrlRow(true);
    axios.get<MigrationReviewPosition>(`/api/blacklist/migrationReviewRows/${rowId}/position`)
      .then(({ data }) => {
        if (!isCurrent) {
          return;
        }

        if (data.row && data.isInUnimportedQueue === false) {
          setRowCount(data.count);
          setSelectedRow(cleanRow(data.row));
          setIsViewingImportedRow(true);
          return;
        }

        setIsViewingImportedRow(false);
        setRowCount(data.count);
        setPagination((current) => (
          current.pageIndex === data.pageIndex ? current : { ...current, pageIndex: data.pageIndex }
        ));
      })
      .catch(() => {
        if (!isCurrent) {
          return;
        }

        setPagination((current) => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
        setIsViewingImportedRow(false);
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.delete(reviewRowIdQueryParam);
          return next;
        }, { replace: true });
      })
      .finally(() => {
        if (isCurrent) {
          setIsResolvingUrlRow(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [searchParams, selectedRow?.id, setSearchParams]);

  useEffect(() => {
    if (isNavigatingByPageControl.current) {
      return;
    }

    if (!selectedRow || parsePositiveInt(searchParams.get(reviewRowIdQueryParam)) === selectedRow.id) {
      return;
    }

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set(reviewRowIdQueryParam, String(selectedRow.id));
      return next;
    }, { replace: true });
  }, [searchParams, selectedRow, setSearchParams]);

  useEffect(() => {
    const firstAvailable = bulletinOptions[0]?.index ?? 0;
    setActiveBulletinIndex(firstAvailable);
    setIsBulletinOpen(Boolean(bulletinOptions.length));
    setActivePdfField(null);
    setSharedBulletinRecords([]);
    setLoadedSharedRecordsRowId(null);
    setHoveredReviewFieldId("");
    clearValidationErrors();
  }, [selectedRow?.id]);

  useEffect(() => {
    setLoadedBulletinViewerUrl(activeBulletinViewerUrl ? "" : activeBulletinViewerUrl);
  }, [activeBulletinViewerUrl]);

  const loadSharedBulletinRecords = useCallback(async (rowId?: number) => {
    const requestId = sharedRecordsRequestId.current + 1;
    sharedRecordsRequestId.current = requestId;

    if (!rowId) {
      setSharedBulletinRecords([]);
      setLoadedSharedRecordsRowId(null);
      return;
    }

    setIsLoadingSharedRecords(true);
    setLoadedSharedRecordsRowId(null);
    try {
      const { data } = await axios.get<SharedBulletinRecord[]>(`/api/blacklist/migrationReviewRows/${rowId}/sharedBulletinRecords`);
      if (sharedRecordsRequestId.current !== requestId) {
        return;
      }

      setSharedBulletinRecords(data || []);
    } catch {
      if (sharedRecordsRequestId.current !== requestId) {
        return;
      }

      setSharedBulletinRecords([]);
    } finally {
      if (sharedRecordsRequestId.current !== requestId) {
        return;
      }

      setLoadedSharedRecordsRowId(rowId);
      setIsLoadingSharedRecords(false);
    }
  }, []);

  useEffect(() => {
    void loadSharedBulletinRecords(selectedRow?.id);
  }, [loadSharedBulletinRecords, selectedRow?.id]);

  useEffect(() => {
    if (!selectedRow) {
      aiPrefillRequestId.current += 1;
      setIsPrefillLoading(false);
      setIsPrefillModalOpen(false);
      setPrefillSuggestions([]);
      setPrefillDecisions({});
      setPrefillWarnings([]);
      setPrefillError("");
      return;
    }

    if (selectedRow.imported) {
      aiPrefillRequestId.current += 1;
      setIsPrefillLoading(false);
      setIsPrefillModalOpen(false);
      setPrefillSuggestions([]);
      setPrefillDecisions({});
      setPrefillWarnings([]);
      setPrefillError("");
    }

    setPrefilledFieldsByRow((current) => {
      if (current[selectedRow.id]) {
        return current;
      }

      return {
        ...current,
        [selectedRow.id]: buildPrefilledFieldMap(selectedRow)
      };
    });
  }, [selectedRow]);

  useEffect(() => {
    const fetchLookups = async () => {
      const [statesResponse, companyTypesResponse] = await Promise.all([
        axios.get<State[]>("/api/blacklist/getStates"),
        axios.get<CompanyType[]>("/api/blacklist/getCompanyTypes")
      ]);
      setStates(statesResponse.data);
      setCompanyTypes(companyTypesResponse.data);
    };
    fetchLookups();
  }, []);

  const openBulletin = (row: BlacklistMigrationReviewRow, index: number) => {
    setSelectedRow(cleanRow(row));
    setActiveBulletinIndex(index);
    setIsBulletinOpen(true);
  };

  const updateSelected = useCallback((updates: Partial<BlacklistMigrationReviewRow>) => {
    setSelectedRow((row) => (row && !row.imported ? cleanRow({ ...row, ...updates }) : row));
  }, []);

  const getPrefillSuggestionForField = useCallback((fieldId: string, displayedValue?: string) => {
    if (fieldId === "notes") {
      return undefined;
    }

    const normalizedFieldId = fieldId === "companyTypeId" ? "companyType" : fieldId === "propertyStateId" ? "propertyState" : fieldId === "propertyCountyId" ? "propertyCounty" : fieldId;
    const normalizedValue = normalizeLookupValue(displayedValue);

    return prefillSuggestions.find((item) => {
      const suggestionFieldId = getReviewFieldIdForSuggestion(item.key);
      return (
        isReviewFieldMatch(suggestionFieldId, fieldId) ||
        item.key === normalizedFieldId ||
        (normalizedValue && normalizeLookupValue(item.value) === normalizedValue)
      );
    });
  }, [prefillSuggestions]);

  const selectEvidenceForField = useCallback((fieldId: string, displayedValue?: string) => {
    const suggestion = getPrefillSuggestionForField(fieldId, displayedValue);

    if (!suggestion) {
      setActivePdfField(displayedValue ? {
        key: fieldId,
        label: fieldId,
        value: displayedValue,
        pageNumber: null,
        sourceChunkId: null,
        sourceText: displayedValue,
        confidence: "low",
        highlights: []
      } : null);
      return;
    }

    if (suggestion.bulletinIndex !== undefined && suggestion.bulletinIndex !== null) {
      setActiveBulletinIndex(suggestion.bulletinIndex);
      setIsBulletinOpen(true);
    }

    setActivePdfField(toPdfFieldResult(suggestion));
  }, [getPrefillSuggestionForField]);

  const isReviewFieldCrossHighlightable = useCallback((fieldId: string) => {
    if (!selectedRow) {
      return false;
    }

    const status = fieldReviewByRow[selectedRow.id]?.[fieldId];
    const isPrefilled = Boolean(prefilledFieldsByRow[selectedRow.id]?.[fieldId]);
    return status === "accepted" || (isPrefilled && status !== "rejected" && status !== "notApplicable");
  }, [fieldReviewByRow, prefilledFieldsByRow, selectedRow]);

  const getReviewFieldHoverProps = useCallback((fieldId: string): ReviewFieldHoverProps => ({
    onMouseEnter: () => isReviewFieldCrossHighlightable(fieldId) && setHoveredReviewFieldId(fieldId),
    onMouseLeave: () => setHoveredReviewFieldId((current) => isReviewFieldMatch(current, fieldId) ? "" : current),
    onClick: () => undefined,
    onFocus: () => undefined
  }), [isReviewFieldCrossHighlightable]);

  const getReviewFieldHighlightSx = useCallback((fieldId: string, options?: { wrapperOnly?: boolean }): SxProps<Theme> => {
    const hasAiSuggestion = Boolean(getPrefillSuggestionForField(fieldId));
    const isHoveredMatch = isReviewFieldCrossHighlightable(fieldId) && isReviewFieldMatch(hoveredReviewFieldId, fieldId);
    const wrapperOnly = Boolean(options?.wrapperOnly);

    if (!hasAiSuggestion && !isHoveredMatch) {
      return {};
    }

    return {
      position: "relative",
      borderRadius: 1,
      bgcolor: isHoveredMatch ? "info.lighter" : undefined,
      outline: isHoveredMatch ? "2px solid" : undefined,
      outlineColor: isHoveredMatch ? "info.main" : undefined,
      outlineOffset: isHoveredMatch ? 2 : undefined,
      transition: "background-color 120ms ease, box-shadow 120ms ease, outline-color 120ms ease",
      ...(!wrapperOnly ? {
        "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
          bgcolor: isHoveredMatch ? "info.lighter" : undefined,
          boxShadow: isHoveredMatch ? 3 : undefined
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: isHoveredMatch || hasAiSuggestion ? "info.main !important" : undefined
          }
        }
      } : {}),
      ...(wrapperOnly ? {
        boxShadow: isHoveredMatch ? 3 : undefined,
        bgcolor: isHoveredMatch ? "info.lighter" : undefined,
        border: hasAiSuggestion ? "1px solid" : undefined,
        borderColor: hasAiSuggestion ? "info.main" : undefined,
        "& .MuiOutlinedInput-root fieldset": {
          borderColor: "divider !important"
        }
      } : {}),
      "& .MuiFormControlLabel-root, & .MuiToggleButtonGroup-root": {
        borderRadius: 1,
        bgcolor: isHoveredMatch ? "info.lighter" : undefined
      },
      "& .MuiToggleButton-root": {
        borderColor: isHoveredMatch || hasAiSuggestion ? "info.main" : undefined
      }
    } as SxProps<Theme>;
  }, [getPrefillSuggestionForField, hoveredReviewFieldId, isReviewFieldCrossHighlightable]);

  const handleBulletinPdfLoadStart = useCallback((viewerUrl: string) => {
    setLoadedBulletinViewerUrl((current) => (current === viewerUrl ? "" : current));
  }, []);

  const handleBulletinPdfLoadComplete = useCallback((viewerUrl: string) => {
    setLoadedBulletinViewerUrl(viewerUrl);
  }, []);

  const handleCopyRawValue = useCallback(async (value: string) => {
    const copyValue = cleanText(value) || value;
    if (!copyValue) {
      setCopyMessage("Nothing to copy");
      return;
    }

    try {
      await copyTextToClipboard(copyValue);
      setCopyMessage("Copied to clipboard");
    } catch {
      setCopyMessage("Could not copy to clipboard");
    }
  }, []);

  const clearRejectedFieldValue = useCallback((fieldId: string) => {
    if (!selectedRow) {
      return;
    }

    const clearIndexedField = (fieldPrefix: "propertyLot" | "propertyParcelId", currentValue?: string) => {
      const indexText = fieldId.slice(fieldPrefix.length + 1);
      const index = Number(indexText);
      if (!Number.isInteger(index) || index < 0) {
        return false;
      }

      const values = splitStoredValues(currentValue);
      if (!values[index]) {
        return true;
      }

      values[index] = "";
      updateSelected({ [fieldPrefix]: joinStoredValues(values) } as Partial<BlacklistMigrationReviewRow>);
      return true;
    };

    if (fieldId.startsWith("propertyLot:") && clearIndexedField("propertyLot", selectedRow.propertyLot)) {
      return;
    }

    if (fieldId.startsWith("propertyParcelId:") && clearIndexedField("propertyParcelId", selectedRow.propertyParcelId)) {
      return;
    }

    const updatesByField: Record<string, Partial<BlacklistMigrationReviewRow>> = {
      targetEntityType: { targetEntityType: undefined },
      notes: { notes: undefined },
      lookupCode: { lookupCode: undefined },
      taxId: { taxId: undefined },
      personTitle: { personTitle: undefined },
      personFirstName: { personFirstName: undefined },
      personMiddleName: { personMiddleName: undefined },
      personLastName: { personLastName: undefined },
      personSuffix: { personSuffix: undefined },
      personIsNotary: { personIsNotary: false },
      personIsAttorney: { personIsAttorney: false, personYearAdmittedToBar: undefined },
      personYearAdmittedToBar: { personYearAdmittedToBar: undefined },
      companyName: { companyName: undefined },
      companyTypeId: { companyTypeId: undefined },
      propertyAddress: { propertyAddress: undefined },
      propertyUnit: { propertyUnit: undefined },
      propertyCity: { propertyCity: undefined },
      propertyStateId: { propertyStateId: undefined, propertyCountyId: undefined },
      propertyZip: { propertyZip: undefined },
      propertyCountyId: { propertyCountyId: undefined },
      propertySection: { propertySection: undefined },
      propertyBlock: { propertyBlock: undefined },
      propertySubdivision: { propertySubdivision: undefined }
    };

    updateSelected(updatesByField[fieldId] || {});
  }, [selectedRow, updateSelected]);

  const acceptSuggestedField = useCallback((fieldId: string) => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    setFieldReviewByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        [fieldId]: "accepted"
      }
    }));
  }, [selectedRow]);

  const clearFieldReview = useCallback((fieldId: string) => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    setFieldReviewByRow((current) => {
      const rowReview = { ...(current[selectedRow.id] || {}) };
      delete rowReview[fieldId];
      return {
        ...current,
        [selectedRow.id]: rowReview
      };
    });
    setRejectedCopiesByRow((current) => {
      const rowCopies = { ...(current[selectedRow.id] || {}) };
      delete rowCopies[fieldId];
      return {
        ...current,
        [selectedRow.id]: rowCopies
      };
    });
  }, [selectedRow]);

  const rejectSuggestedField = useCallback((fieldId: string, displayedValue: unknown) => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    const copyValue = formatDisplayValue(displayedValue);
    setFieldReviewByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        [fieldId]: "rejected"
      }
    }));
    setRejectedCopiesByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        [fieldId]: copyValue
      }
    }));
    clearRejectedFieldValue(fieldId);
    void handleCopyRawValue(copyValue);
  }, [clearRejectedFieldValue, handleCopyRawValue, selectedRow]);

  const markFieldNotApplicable = useCallback((fieldId: string) => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    setFieldReviewByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        [fieldId]: "notApplicable"
      }
    }));
    setPrefilledFieldsByRow((current) => {
      const rowFields = { ...(current[selectedRow.id] || {}) };
      delete rowFields[fieldId];
      return {
        ...current,
        [selectedRow.id]: rowFields
      };
    });
    clearRejectedFieldValue(fieldId);
  }, [clearRejectedFieldValue, selectedRow]);

  const loadAiPrefillSuggestions = useCallback(async (row: BlacklistMigrationReviewRow, openModal = false) => {
    if (row.imported) {
      aiPrefillRequestId.current += 1;
      setIsPrefillLoading(false);
      setIsPrefillModalOpen(false);
      setPrefillError("");
      setPrefillWarnings([]);
      setPrefillSuggestions([]);
      setPrefillDecisions({});
      return;
    }

    const requestId = aiPrefillRequestId.current + 1;
    aiPrefillRequestId.current = requestId;

    if (openModal) {
      setIsPrefillModalOpen(true);
    }
    setIsPrefillLoading(true);
    setPrefillError("");
    setPrefillWarnings([]);
    setPrefillSuggestions([]);
    setPrefillDecisions({});

    try {
      const { data } = await axios.post<AiPrefillResponse>(
        `/api/blacklist/migrationReviewRows/${row.id}/suggestPrefill`,
        cleanRow(row)
      );
      if (aiPrefillRequestId.current !== requestId) {
        return;
      }

      const suggestions = (data.suggestions || []).filter((suggestion) => cleanText(suggestion.key) && cleanText(suggestion.value));
      setPrefillSuggestions(suggestions);
      setPrefillWarnings(data.warnings || []);
      setPrefillDecisions(
        suggestions.reduce<Record<string, PrefillDecision>>((decisions, suggestion) => {
          decisions[suggestion.key] = "approved";
          return decisions;
        }, {})
      );

      if (openModal && !suggestions.length && !(data.warnings || []).length) {
        setPrefillWarnings(["AI did not find any confident prefill suggestions for this row."]);
      }
    } catch (err: any) {
      if (aiPrefillRequestId.current !== requestId) {
        return;
      }

      const response = err?.response?.data;
      if (openModal) {
        setPrefillError(typeof response === "string" ? response : response?.message || "AI prefill failed. The row was not changed.");
      }
    } finally {
      if (aiPrefillRequestId.current === requestId) {
        setIsPrefillLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    void loadAiPrefillSuggestions(selectedRow, false);
  }, [loadAiPrefillSuggestions, selectedRow?.id, selectedRow?.imported]);

  const setPrefillDecision = useCallback((suggestion: AiPrefillSuggestion, decision: PrefillDecision) => {
    setPrefillDecisions((current) => ({ ...current, [suggestion.key]: decision }));
    if (decision === "rejected") {
      void handleCopyRawValue(suggestion.value);
    }
  }, [handleCopyRawValue]);

  const mapAiPrefillSuggestion = useCallback((suggestion: AiPrefillSuggestion, currentRow: BlacklistMigrationReviewRow, pendingUpdates: Partial<BlacklistMigrationReviewRow> = {}) => {
    const directStringFields: Record<string, keyof BlacklistMigrationReviewRow> = {
      lookupCode: "lookupCode",
      taxId: "taxId",
      personTitle: "personTitle",
      personFirstName: "personFirstName",
      personMiddleName: "personMiddleName",
      personLastName: "personLastName",
      personSuffix: "personSuffix",
      personYearAdmittedToBar: "personYearAdmittedToBar",
      companyName: "companyName",
      propertyAddress: "propertyAddress",
      propertyUnit: "propertyUnit",
      propertyCity: "propertyCity",
      propertyZip: "propertyZip",
      propertySection: "propertySection",
      propertyBlock: "propertyBlock",
      propertySubdivision: "propertySubdivision"
    };

    const value = cleanText(suggestion.value);
    if (!value) {
      return null;
    }

    const normalizedValue = normalizeLookupValue(value);
    if (suggestion.key === "propertyLot") {
      const existingLots = splitStoredValues((pendingUpdates.propertyLot as string | undefined) || currentRow.propertyLot);
      const nextLots = [...existingLots];
      nextLots[0] = value;
      return {
        updates: { propertyLot: joinStoredValues(nextLots) } as Partial<BlacklistMigrationReviewRow>,
        fieldId: "propertyLot:0"
      };
    }

    if (suggestion.key === "propertyParcelId") {
      const existingParcelIds = splitStoredValues((pendingUpdates.propertyParcelId as string | undefined) || currentRow.propertyParcelId);
      const nextParcelIds = [...existingParcelIds];
      nextParcelIds[0] = value;
      return {
        updates: { propertyParcelId: joinStoredValues(nextParcelIds) } as Partial<BlacklistMigrationReviewRow>,
        fieldId: "propertyParcelId:0"
      };
    }

    const directField = directStringFields[suggestion.key];
    if (directField) {
      return {
        updates: { [directField]: value } as Partial<BlacklistMigrationReviewRow>,
        fieldId: getReviewFieldIdForSuggestion(suggestion.key)
      };
    }

    if (suggestion.key === "targetEntityType") {
      if (["individual", "person"].includes(normalizedValue)) {
        return { updates: { targetEntityType: BlacklistEntityType.Person }, fieldId: "targetEntityType" };
      }

      if (["entity", "company", "business", "corporation", "corp", "llc"].includes(normalizedValue)) {
        return { updates: { targetEntityType: BlacklistEntityType.Company }, fieldId: "targetEntityType" };
      }

      if (normalizedValue === "property") {
        return { updates: { targetEntityType: BlacklistEntityType.Property }, fieldId: "targetEntityType" };
      }

      return null;
    }

    if (suggestion.key === "personIsAttorney" || suggestion.key === "personIsNotary") {
      const parsed = parseSuggestedBoolean(value);
      return parsed !== undefined ? { updates: { [suggestion.key]: parsed } as Partial<BlacklistMigrationReviewRow>, fieldId: suggestion.key } : null;
    }

    if (suggestion.key === "companyType") {
      const companyType = companyTypes.find((type) => normalizeLookupValue(type.name) === normalizedValue || normalizeLookupValue(type.id) === normalizedValue);
      return companyType ? { updates: { companyTypeId: companyType.id }, fieldId: "companyTypeId" } : null;
    }

    if (suggestion.key === "propertyState") {
      const state = states.find(
        (state) =>
          normalizeLookupValue(state.name) === normalizedValue ||
          normalizeLookupValue(state.abbreviation) === normalizedValue ||
          normalizeLookupValue(state.id) === normalizedValue
      );
      if (!state) {
        return null;
      }

      return {
        updates: {
          propertyStateId: state.id,
          ...((currentRow.propertyStateId !== state.id) ? { propertyCountyId: undefined } : {})
        },
        fieldId: "propertyStateId"
      };
    }

    if (suggestion.key === "propertyCounty") {
      const stateId = pendingUpdates.propertyStateId || currentRow.propertyStateId;
      const availableCounties = stateId ? states.find((state) => state.id === stateId)?.counties || [] : states.flatMap((state) => state.counties || []);
      const county = availableCounties.find((county) => normalizeLookupValue(county.name) === normalizedValue || normalizeLookupValue(county.id) === normalizedValue);
      if (!county) {
        return null;
      }

      return {
        updates: {
          propertyCountyId: county.id,
          ...((!pendingUpdates.propertyStateId && county.stateId) ? { propertyStateId: county.stateId } : {})
        },
        fieldId: "propertyCountyId"
      };
    }

    return null;
  }, [companyTypes, states]);

  const applySingleAiSuggestion = useCallback((suggestion: AiPrefillSuggestion) => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    const mapped = mapAiPrefillSuggestion(suggestion, selectedRow);
    if (!mapped) {
      setPrefillError("This AI suggestion could not be mapped to an editable field.");
      return;
    }

    updateSelected(mapped.updates);
    setPrefilledFieldsByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        [mapped.fieldId]: true
      }
    }));
    setFieldReviewByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        [mapped.fieldId]: "accepted"
      }
    }));
    selectEvidenceForField(mapped.fieldId, suggestion.value);
    setCopyMessage(`Applied AI suggestion to ${suggestion.label || mapped.fieldId}.`);
  }, [mapAiPrefillSuggestion, selectEvidenceForField, selectedRow, updateSelected]);

  const renderReviewActions = useCallback((fieldId: string, displayedValue: unknown) => {
    if (!selectedRow) {
      return null;
    }

    if (selectedRow.imported) {
      return null;
    }

    const prefilledFields = prefilledFieldsByRow[selectedRow.id] || {};
    const fieldReview = fieldReviewByRow[selectedRow.id] || {};
    const status = fieldReview[fieldId];
    const isPrefilled = Boolean(prefilledFields[fieldId]);
    const aiSuggestion = getPrefillSuggestionForField(fieldId, formatDisplayValue(displayedValue));
    if (!isPrefilled && !status && !aiSuggestion) {
      return null;
    }

    const hasReviewActions = isPrefilled || Boolean(status);
    const showAllReviewActions = hasReviewActions && !status;
    const buttonSx = { p: 0.25, borderRadius: 0.5 };

    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        {...getReviewFieldHoverProps(fieldId)}
        onClick={(event) => event.stopPropagation()}
      >
        {aiSuggestion && (
          <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            sx={{
              position: "absolute",
              right: 0,
              top: -24,
              zIndex: 2,
              border: "1px solid",
              borderColor: "info.light",
              bgcolor: "info.lighter",
              borderRadius: 0.75,
              px: 0.5,
              py: 0.15
            }}
          >
            <Tooltip title="Show AI evidence in the bulletin">
              <Box
                component="button"
                type="button"
                onClick={() => selectEvidenceForField(fieldId, aiSuggestion.value)}
                onMouseEnter={() => setHoveredReviewFieldId(fieldId)}
                onMouseLeave={() => setHoveredReviewFieldId((current) => isReviewFieldMatch(current, fieldId) ? "" : current)}
                sx={{
                  m: 0,
                  p: 0,
                  border: 0,
                  bgcolor: "transparent",
                  color: "info.dark",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 800,
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  fontFamily: "inherit"
                }}
              >
                AI suggestion
              </Box>
            </Tooltip>
            <Tooltip title={`Use AI suggestion: ${aiSuggestion.value}`}>
              <IconButton
                aria-label={`use AI suggestion for ${fieldId}`}
                size="small"
                disableRipple
                onClick={() => applySingleAiSuggestion(aiSuggestion)}
                sx={{
                  p: 0,
                  borderRadius: 0.5,
                  color: "success.main"
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        {hasReviewActions && (showAllReviewActions || status === "accepted") && (
          <Tooltip title={status === "accepted" ? "Undo keep" : "Keep this value"}>
            <IconButton
              aria-label={`accept ${fieldId}`}
              size="small"
              disableRipple
              onClick={() => status === "accepted" ? clearFieldReview(fieldId) : acceptSuggestedField(fieldId)}
              sx={{
                ...buttonSx,
                color: "success.main"
              }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {hasReviewActions && (showAllReviewActions || status === "rejected") && (
          <Tooltip title={status === "rejected" ? "Undo reject" : "Reject, clear this field, and copy it"}>
            <IconButton
              aria-label={`reject ${fieldId}`}
              size="small"
              disableRipple
              onClick={() => status === "rejected" ? clearFieldReview(fieldId) : rejectSuggestedField(fieldId, displayedValue)}
              sx={{ ...buttonSx, color: "error.main" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {hasReviewActions && (showAllReviewActions || status === "notApplicable") && (
          <Tooltip title={status === "notApplicable" ? "Undo not applicable" : "Not applicable, clear this field"}>
            <IconButton
              aria-label={`not applicable ${fieldId}`}
              size="small"
              disableRipple
              onClick={() => status === "notApplicable" ? clearFieldReview(fieldId) : markFieldNotApplicable(fieldId)}
              sx={{ ...buttonSx, color: "warning.dark" }}
            >
              <DoNotDisturbAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  }, [acceptSuggestedField, applySingleAiSuggestion, clearFieldReview, fieldReviewByRow, getPrefillSuggestionForField, getReviewFieldHoverProps, markFieldNotApplicable, prefilledFieldsByRow, rejectSuggestedField, selectedRow]);

  const unlinkBulletin = useCallback(async (index: number) => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    setError("");
    setMessage("");
    try {
      const { data } = await axios.post<BlacklistMigrationReviewRow>(`/api/blacklist/migrationReviewRows/${selectedRow.id}/bulletins/${index}/unlink`);
      const cleaned = cleanRow(data);
      setSelectedRow(cleaned);
      setMessage(`Unlinked bulletin ${index + 1}.`);
      await loadSharedBulletinRecords(cleaned.id);
    } catch (err: any) {
      const response = err?.response?.data;
      setError(typeof response === "string" ? response : "Could not unlink this bulletin.");
    }
  }, [loadSharedBulletinRecords, selectedRow]);

  const openEditBulletinModal = useCallback(async (index: number) => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    setIsBulletinEditOpen(true);
    setIsLoadingBulletinEdit(true);
    setError("");
    try {
      const { data } = await axios.get<MigrationBulletinInfo>(`/api/blacklist/migrationReviewRows/${selectedRow.id}/bulletins/${index}`);
      setEditingBulletin(data);
    } catch (err: any) {
      const response = err?.response?.data;
      setError(typeof response === "string" ? response : "Could not load bulletin details.");
      setIsBulletinEditOpen(false);
    } finally {
      setIsLoadingBulletinEdit(false);
    }
  }, [selectedRow]);

  const saveBulletinEdit = useCallback(async () => {
    if (!selectedRow || selectedRow.imported || !editingBulletin) {
      return;
    }

    setIsSavingBulletinEdit(true);
    setError("");
    setMessage("");
    try {
      const { data } = await axios.put<MigrationBulletinInfo>(
        `/api/blacklist/migrationReviewRows/${selectedRow.id}/bulletins/${editingBulletin.index}`,
        {
          number: editingBulletin.number || undefined,
          date: editingBulletin.date || null
        }
      );
      setEditingBulletin(data);
      updateSelected({
        bulletinNum: data.number || undefined,
        bulletinDate: data.date || undefined
      });
      setMessage(`Saved bulletin ${data.id}.`);
      setIsBulletinEditOpen(false);
    } catch (err: any) {
      const response = err?.response?.data;
      setError(typeof response === "string" ? response : "Could not save bulletin details.");
    } finally {
      setIsSavingBulletinEdit(false);
    }
  }, [editingBulletin, selectedRow, updateSelected]);

  const applyPrefillSuggestions = useCallback(() => {
    if (!selectedRow || selectedRow.imported) {
      return;
    }

    const updates: Partial<BlacklistMigrationReviewRow> = {};
    const acceptedFieldIds: string[] = [];
    const approvedSuggestions = prefillSuggestions
      .filter((suggestion) => prefillDecisions[suggestion.key] !== "rejected")
      .sort((left, right) => {
        if (left.key === "propertyState") return -1;
        if (right.key === "propertyState") return 1;
        return 0;
      });

    approvedSuggestions.forEach((suggestion) => {
      const mapped = mapAiPrefillSuggestion(suggestion, selectedRow, updates);
      if (mapped) {
        Object.assign(updates, mapped.updates);
        acceptedFieldIds.push(mapped.fieldId);
      }
    });

    if (!acceptedFieldIds.length) {
      setPrefillError("No approved suggestions could be mapped to editable row fields.");
      return;
    }

    updateSelected(updates);
    setPrefilledFieldsByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        ...acceptedFieldIds.reduce<PrefilledFieldMap>((fields, fieldId) => {
          fields[fieldId] = true;
          return fields;
        }, {})
      }
    }));
    setFieldReviewByRow((current) => ({
      ...current,
      [selectedRow.id]: {
        ...(current[selectedRow.id] || {}),
        ...acceptedFieldIds.reduce<FieldReviewMap>((fields, fieldId) => {
          fields[fieldId] = "accepted";
          return fields;
        }, {})
      }
    }));
    setMessage(`Applied ${acceptedFieldIds.length} AI prefill suggestion${acceptedFieldIds.length === 1 ? "" : "s"}.`);
    setIsPrefillModalOpen(false);
  }, [mapAiPrefillSuggestion, prefillDecisions, prefillSuggestions, selectedRow, updateSelected]);

  const individualInfo: BlacklistNameIndividual = {
    title: selectedRow?.personTitle || "",
    firstName: selectedRow?.personFirstName || "",
    middleName: selectedRow?.personMiddleName || "",
    lastName: selectedRow?.personLastName || "",
    suffix: selectedRow?.personSuffix || "",
    taxID: selectedRow?.taxId || "",
    isAttorney: Boolean(selectedRow?.personIsAttorney),
    isNotary: Boolean(selectedRow?.personIsNotary),
    yearAdmittedToBar: selectedRow?.personYearAdmittedToBar || "",
    notes: selectedRow?.notes || "",
    lookupCode: selectedRow?.lookupCode || ""
  };

  const entityInfo: BlacklistNameEntity = {
    name: selectedRow?.companyName || "",
    companyTypeId: selectedCompanyType as any,
    taxID: selectedRow?.taxId || "",
    notes: selectedRow?.notes || "",
    lookupCode: selectedRow?.lookupCode || ""
  };

  const propertyInfo = useMemo(() => getPropertyInfoFromRow(selectedRow), [selectedRow]);

  const changeIndividualInfo = useCallback(
    (field: string, value: any) => {
      const updatesByField: Record<string, Partial<BlacklistMigrationReviewRow>> = {
        title: { personTitle: value || undefined },
        firstName: { personFirstName: value || undefined },
        middleName: { personMiddleName: value || undefined },
        lastName: { personLastName: value || undefined },
        suffix: { personSuffix: value || undefined },
        taxID: { taxId: value || undefined },
        isAttorney: { personIsAttorney: Boolean(value) },
        isNotary: { personIsNotary: Boolean(value) },
        yearAdmittedToBar: { personYearAdmittedToBar: value || undefined },
        lookupCode: { lookupCode: value || undefined }
      };
      updateSelected(updatesByField[field] || {});
    },
    [updateSelected]
  );

  const changeEntityInfo = useCallback(
    (field: string, value: any) => {
      const updatesByField: Record<string, Partial<BlacklistMigrationReviewRow>> = {
        name: { companyName: value || undefined },
        companyTypeId: { companyTypeId: value?.id || value?.value || undefined },
        taxID: { taxId: value || undefined },
        lookupCode: { lookupCode: value || undefined }
      };
      updateSelected(updatesByField[field] || {});
    },
    [updateSelected]
  );

  const setMigrationPropertyInfo = useCallback(
    (next: PropertyInfo | ((previous: PropertyInfo) => PropertyInfo)) => {
      const nextPropertyInfo = typeof next === "function" ? next(propertyInfo) : next;
      updateSelected({
        propertyAddress: nextPropertyInfo.address || undefined,
        propertyUnit: nextPropertyInfo.aptNo || undefined,
        propertyCity: nextPropertyInfo.city || undefined,
        propertyZip: nextPropertyInfo.zipCode || undefined,
        propertySection: nextPropertyInfo.section || nextPropertyInfo.borough || undefined,
        propertyBlock: nextPropertyInfo.block || undefined,
        propertyLot: joinStoredValues(nextPropertyInfo.lots),
        propertyParcelId: joinStoredValues(nextPropertyInfo.parcelIds),
        propertySubdivision: nextPropertyInfo.condoNumber || undefined
      });
    },
    [propertyInfo, updateSelected]
  );

  const setMigrationState = useCallback(
    (state: State | null) => {
      updateSelected({ propertyStateId: state?.id, propertyCountyId: undefined });
    },
    [updateSelected]
  );

  const setMigrationCounty = useCallback(
    (county: County | null) => {
      updateSelected({ propertyCountyId: county?.id });
    },
    [updateSelected]
  );

  const setMigrationIsIndividual = useCallback(
    (isIndividual: boolean) => {
      updateSelected({ targetEntityType: isIndividual ? BlacklistEntityType.Person : BlacklistEntityType.Company });
    },
    [updateSelected]
  );

  const setBlacklistContactFromPhonebookContact = useCallback(
    (contact: OrderEntryContact, isIndividual: boolean) => {
      if (isIndividual) {
        updateSelected({
          personTitle: contact.title || undefined,
          personFirstName: contact.firstName || undefined,
          personMiddleName: contact.middleName || undefined,
          personLastName: contact.lastName || undefined,
          personSuffix: contact.suffix || undefined,
          lookupCode: contact.lookupCode || undefined
        });
        return;
      }

      updateSelected({
        companyName: contact.name || undefined,
        lookupCode: contact.lookupCode || undefined
      });
    },
    [updateSelected]
  );

  const clearValidationErrors = () => {
    setNameError(false);
    setCompanyError(false);
    setPropertyStateError(false);
    setPropertyCityError(false);
    setPropertyAddressError(false);
    setPropertySblError(false);
  };

  const validateSelectedRowForCommit = () => {
    if (!selectedRow?.targetEntityType) {
      setError("Choose Individual, Entity, or Property before importing this row.");
      return false;
    }

    clearValidationErrors();

    if (selectedRow.targetEntityType === BlacklistEntityType.Person) {
      const isInvalid = !cleanText(selectedRow.personLastName) && !cleanText(selectedRow.taxId);
      setNameError(isInvalid);
      if (isInvalid) {
        setError("Individual rows require a last name or Tax ID.");
      }
      return !isInvalid;
    }

    if (selectedRow.targetEntityType === BlacklistEntityType.Company) {
      const isInvalid = !cleanText(selectedRow.companyName) && !cleanText(selectedRow.taxId);
      setCompanyError(isInvalid);
      if (isInvalid) {
        setError("Entity rows require a company name or Tax ID.");
      }
      return !isInvalid;
    }

    if (selectedRow.targetEntityType === BlacklistEntityType.Property) {
      const hasState = Boolean(selectedRow.propertyStateId);
      const hasCity = Boolean(cleanText(selectedRow.propertyCity));
      const hasAddress = Boolean(cleanText(selectedRow.propertyAddress));
      const hasParcel = Boolean(cleanText(selectedRow.propertyParcelId));
      const hasBlock = Boolean(cleanText(selectedRow.propertyBlock));
      const hasLot = Boolean(cleanText(selectedRow.propertyLot));
      const hasSubdivision = Boolean(cleanText(selectedRow.propertySubdivision));
      const isAddressIncomplete = !hasAddress && !hasParcel && !hasBlock && !hasLot && !hasSubdivision;
      const isSblIncomplete = (hasBlock && !hasLot) || (Boolean(cleanText(selectedRow.propertySection)) && !(hasBlock && hasLot));
      const isInvalid = !hasState || !hasCity || isAddressIncomplete || isSblIncomplete;

      setPropertyStateError(!hasState);
      setPropertyCityError(!hasCity);
      setPropertyAddressError(isAddressIncomplete);
      setPropertySblError(isSblIncomplete);

      if (isInvalid) {
        setError("Property rows require state, city, and at least address, parcel, complete S/B/L, or subdivision.");
      }

      return !isInvalid;
    }

    return true;
  };

  const goToPreviousRow = () => {
    if (!hasPreviousRow) {
      return;
    }

    isNavigatingByPageControl.current = true;
    setIsViewingImportedRow(false);
    setMessage("");
    setAdvanceMessage("");
    setError("");
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete(reviewRowIdQueryParam);
      return next;
    }, { replace: true });
    setPagination((current) => ({ ...current, pageIndex: current.pageIndex - 1 }));
  };

  const goToNextRow = () => {
    if (!hasNextRow) {
      return;
    }

    isNavigatingByPageControl.current = true;
    setIsViewingImportedRow(false);
    setMessage("");
    setAdvanceMessage("");
    setError("");
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete(reviewRowIdQueryParam);
      return next;
    }, { replace: true });
    setPagination((current) => {
      if (isSelectedRowImported) {
        return current;
      }

      return { ...current, pageIndex: current.pageIndex + 1 };
    });
  };

  const saveRow = async () => {
    if (!selectedRow || selectedRow.imported) return;
    setIsSaving(true);
    setError("");
    setMessage("");
    setAdvanceMessage("");
    try {
      if (selectedRow.targetRecordId && selectedRow.targetEntityType === BlacklistEntityType.Person) {
        await axios.post(`/api/blacklist/appendToPersonRecord?entityId=${selectedRow.targetRecordId}`, {
          person: {
            title: selectedRow.personTitle || "",
            firstName: selectedRow.personFirstName || "",
            middleName: selectedRow.personMiddleName || "",
            lastName: selectedRow.personLastName || "",
            suffix: selectedRow.personSuffix || "",
            isAttorney: Boolean(selectedRow.personIsAttorney),
            taxID: selectedRow.taxId || "",
            isNotary: Boolean(selectedRow.personIsNotary),
            yearAdmittedToBar: selectedRow.personYearAdmittedToBar || "",
            notes: selectedRow.notes || "",
            lookupCode: selectedRow.lookupCode || ""
          },
          locations: [],
          bulletinsWithBase64: []
        });
      } else if (selectedRow.targetRecordId && selectedRow.targetEntityType === BlacklistEntityType.Company) {
        await axios.post(`/api/blacklist/appendToCompanyRecord?entityId=${selectedRow.targetRecordId}`, {
          company: {
            name: selectedRow.companyName || "",
            companyTypeId: selectedRow.companyTypeId || null,
            taxID: selectedRow.taxId || "",
            notes: selectedRow.notes || "",
            lookupCode: selectedRow.lookupCode || ""
          },
          locations: [],
          bulletinsWithBase64: []
        });
      } else if (selectedRow.targetRecordId && selectedRow.targetEntityType === BlacklistEntityType.Property) {
        await axios.post(`/api/blacklist/appendToPropertyRecord?entityId=${selectedRow.targetRecordId}`, {
          property: {
            address: selectedRow.propertyAddress || "",
            city: selectedRow.propertyCity || "",
            section: selectedRow.propertySection || "",
            block: selectedRow.propertyBlock || "",
            unit: selectedRow.propertyUnit || "",
            zip: selectedRow.propertyZip || "",
            lots: splitStoredValues(selectedRow.propertyLot).map((lot) => ({ lotNumber: lot.trim() })).filter((lot) => Boolean(lot.lotNumber)),
            parcels: splitStoredValues(selectedRow.propertyParcelId).map((parcel) => ({ parcelId: parcel.trim() })).filter((parcel) => Boolean(parcel.parcelId)),
            subdivision: selectedRow.propertySubdivision || "",
            countyId: selectedRow.propertyCountyId,
            stateId: selectedRow.propertyStateId,
            latitude: 0,
            longitude: 0,
            geocodedAddress: "",
            notes: selectedRow.notes || ""
          },
          locations: [],
          bulletinsWithBase64: []
        });
      }

      const { data } = await axios.put<BlacklistMigrationReviewRow>(`/api/blacklist/migrationReviewRows/${selectedRow.id}`, cleanRow(selectedRow));
      const savedReviewRow = cleanRow({
        ...data,
        targetRecordId: selectedRow.targetRecordId,
        targetEntityType: selectedRow.targetEntityType
      });

      if (selectedRow.targetRecordId && selectedRow.targetEntityType) {
        const { data: linkResult } = await axios.post(`/api/blacklist/migrationReviewRows/${selectedRow.id}/linkExisting`, {
          targetEntityType: selectedRow.targetEntityType,
          targetRecordId: selectedRow.targetRecordId
        });
        setSelectedRow(cleanRow({
          ...savedReviewRow,
          imported: true,
          targetEntityType: linkResult.targetEntityType,
          targetRecordId: linkResult.targetRecordId,
          commitError: undefined
        }));
        setIsViewingImportedRow(true);
        setRowCount((current) => Math.max(0, current - 1));
        setMessage(`Updated existing ${entityLabels[linkResult.targetEntityType as BlacklistEntityType]} record ${linkResult.targetRecordId} and marked this row imported.`);
      } else {
        setSelectedRow(savedReviewRow);
        setMessage("Draft saved.");
      }
    } catch (err: any) {
      setError(err?.response?.data || "Could not save this row.");
    } finally {
      setIsSaving(false);
    }
  };

  const linkExistingRecord = async (record: SharedBulletinRecord) => {
    if (!selectedRow || selectedRow.imported) return;

    const recordKey = `${record.entityType}-${record.entityId}`;
    setLinkingExistingRecordKey(recordKey);
    setError("");
    setMessage("");
    setAdvanceMessage("");
    try {
      if (record.entityType === BlacklistEntityType.Person) {
        const { data } = await axios.get<PersonRecord>(`/api/blacklist/GetPersonRecordById?id=${record.entityId}`);
        updateSelected({
          ...emptyTargetFieldValues,
          targetEntityType: BlacklistEntityType.Person,
          targetRecordId: record.entityId,
          lookupCode: data.lookupCode || undefined,
          taxId: data.taxID || undefined,
          notes: data.notes || undefined,
          personTitle: data.title || undefined,
          personFirstName: data.firstName || undefined,
          personMiddleName: data.middleName || undefined,
          personLastName: data.lastName || undefined,
          personSuffix: data.suffix || undefined,
          personIsAttorney: Boolean(data.isAttorney),
          personIsNotary: Boolean(data.isNotary),
          personYearAdmittedToBar: data.yearAdmittedToBar || undefined
        });
      } else if (record.entityType === BlacklistEntityType.Company) {
        const { data } = await axios.get<CompanyRecord>(`/api/blacklist/getCompanyRecordById?id=${record.entityId}`);
        updateSelected({
          ...emptyTargetFieldValues,
          targetEntityType: BlacklistEntityType.Company,
          targetRecordId: record.entityId,
          lookupCode: data.lookupCode || undefined,
          taxId: data.taxID || undefined,
          notes: data.notes || undefined,
          companyName: data.name || undefined,
          companyTypeId: data.companyTypeId || undefined
        });
      } else if (record.entityType === BlacklistEntityType.Property) {
        const { data } = await axios.get<PropertyRecord>(`/api/blacklist/getPropertyRecordById?id=${record.entityId}`);
        updateSelected({
          ...emptyTargetFieldValues,
          targetEntityType: BlacklistEntityType.Property,
          targetRecordId: record.entityId,
          notes: data.notes || undefined,
          propertyAddress: data.address || undefined,
          propertyUnit: data.unit || undefined,
          propertyCity: data.city || undefined,
          propertyZip: data.zip || undefined,
          propertySection: data.section || undefined,
          propertyBlock: data.block || undefined,
          propertySubdivision: data.subdivision || undefined,
          propertyStateId: data.stateId || undefined,
          propertyCountyId: data.countyId || undefined,
          propertyLot: joinStoredValues(data.lots?.map((lot) => lot.lotNumber) || []),
          propertyParcelId: joinStoredValues(data.parcels?.map((parcel) => parcel.parcelId) || [])
        });
      }

      setMessage(`Loaded existing ${entityLabels[record.entityType]} record ${record.entityId} into the form. Review migration checklist and AI suggestions before saving changes to that record.`);
    } catch (err: any) {
      const response = err?.response?.data;
      setAdvanceMessage("");
      setError(typeof response === "string" ? response : "Could not load the existing blacklist record into the form.");
    } finally {
      setLinkingExistingRecordKey("");
    }
  };

  const commitRow = async () => {
    if (!selectedRow || selectedRow.imported) return;
    setError("");
    setMessage("");
    setAdvanceMessage("");
    if (!validateSelectedRowForCommit()) {
      return;
    }

    setIsSaving(true);
    try {
      await axios.put<BlacklistMigrationReviewRow>(`/api/blacklist/migrationReviewRows/${selectedRow.id}`, cleanRow(selectedRow));
      const { data } = selectedRow.targetRecordId
        ? await axios.post(`/api/blacklist/migrationReviewRows/${selectedRow.id}/linkExisting`, {
          targetEntityType: selectedRow.targetEntityType,
          targetRecordId: selectedRow.targetRecordId
        })
        : await axios.post(`/api/blacklist/migrationReviewRows/${selectedRow.id}/commit`);
      setSelectedRow(cleanRow({
        ...selectedRow,
        imported: true,
        targetEntityType: data.targetEntityType,
        targetRecordId: data.targetRecordId,
        commitError: undefined
      }));
      setIsViewingImportedRow(true);
      setRowCount((current) => Math.max(0, current - 1));
      setMessage(`${selectedRow.targetRecordId ? "Linked to existing" : "Imported"} ${entityLabels[data.targetEntityType as BlacklistEntityType]} record ${data.targetRecordId}.`);
    } catch (err: any) {
      const response = err?.response?.data;
      setError(typeof response === "string" ? response : "Import failed. The row was marked with the error from the server.");
      await fetchRows();
    } finally {
      setIsSaving(false);
    }
  };

  const potentialMatchingRecords = useMemo(
    // An older running API only returns close matches and does not include this flag.
    () => sharedBulletinRecords.filter((record) => record.isPotentialMatch !== false),
    [sharedBulletinRecords]
  );
  const otherSharedBulletinRecords = useMemo(
    () => sharedBulletinRecords.filter((record) => record.isPotentialMatch === false),
    [sharedBulletinRecords]
  );

  const renderExistingRecordsPanel = (
    records: SharedBulletinRecord[],
    title: string,
    description: string,
    options?: { showLoading?: boolean; tone?: "info" | "warning" }
  ) => {
    const tone = options?.tone || "info";
    if (!options?.showLoading && records.length === 0) {
      return null;
    }

    return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        borderColor: tone === "warning" ? "warning.light" : "info.light",
        bgcolor: tone === "warning" ? "warning.lighter" : "info.lighter"
      }}
    >
      <Stack spacing={1}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Box>

        {options?.showLoading && (
          <Typography variant="body2" color="text.secondary">
            Checking shared bulletins...
          </Typography>
        )}

        {!options?.showLoading && records.map((record) => {
          const recordKey = `${record.entityType}-${record.entityId}`;
          return (
            <Paper key={recordKey} variant="outlined" sx={{ p: 1.25, borderRadius: 1.25, bgcolor: "background.paper" }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={entityLabels[record.entityType]} color={tone} variant="outlined" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                      {record.displayName || `${entityLabels[record.entityType]} ${record.entityId}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID {record.entityId}
                    </Typography>
                  </Stack>
                  {record.details && (
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      {record.details}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Shared bulletin{record.sharedBulletinIds.length === 1 ? "" : "s"}: {record.sharedBulletinIds.join(", ")}
                  </Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                    {(record.matchReasons?.length
                      ? record.matchReasons
                      : record.isPotentialMatch === false
                        ? ["Bulletin only; no text match"]
                        : ["Potential text match; reason unavailable"]
                    ).map((reason) => (
                      <Chip
                        key={reason}
                        size="small"
                        label={reason}
                        color={record.isPotentialMatch === false ? "warning" : "success"}
                        variant="outlined"
                        sx={{ height: "auto", "& .MuiChip-label": { py: 0.25, whiteSpace: "normal" } }}
                      />
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="text"
                    size="small"
                    component="a"
                    href={`/view-bulletins?entityType=${record.entityType}&entityId=${record.entityId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    component="a"
                    href={getBlacklistRecordUrl(record)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={isSelectedRowImported || Boolean(linkingExistingRecordKey)}
                    onClick={() => void linkExistingRecord(record)}
                  >
                    {linkingExistingRecordKey === recordKey ? "Loading..." : "Link existing"}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
    );
  };

  const potentialMatchingRecordsPanel = renderExistingRecordsPanel(
    potentialMatchingRecords,
    "Potential matching existing records",
    "These share a bulletin and look like a close text match. Load one into the form, compare it to the migration checklist and AI suggestions, then save changes to that record.",
    { showLoading: isLoadingSharedRecords, tone: "info" }
  );
  const otherSharedBulletinRecordsPanel = renderExistingRecordsPanel(
    otherSharedBulletinRecords,
    "Other records with this bulletin",
    "These share the bulletin but do not look like a close text match. They are shown for awareness before creating or linking a record.",
    { tone: "warning" }
  );

  return (
    <Page title="Blacklist Migration Review" width="100%">
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: "100%",
          maxWidth: "none",
          pt: 0,
          mt: 0,
          px: { xs: 1, md: 2 }
        }}
      >
        <Stack spacing={1} sx={{ pt: 0, mt: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            spacing={1}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{
              position: { lg: "sticky" },
              top: { lg: `${dashboardStickyTop}px` },
              zIndex: { lg: 5 },
              height: { lg: `${dashboardReviewHeaderHeight}px` },
              boxSizing: "border-box",
              px: { lg: 0.5 },
              py: { lg: 0.75 },
              pointerEvents: { lg: "none" }
            }}
          >
            <Box
              sx={{
                pointerEvents: { lg: "auto" },
                bgcolor: "background.default",
                borderRadius: 1,
                pr: 1
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Blacklist Migration Review</Typography>
              <Typography variant="body2" color="text.secondary">
                {isSelectedRowImported ? `Already imported | ${rowCount} unimported rows` : `Row ${currentRowNumber} of ${rowCount} unimported rows`}
                {selectedRow ? ` | Old row ${selectedRow.sourceBlacklistId || selectedRow.id}` : ""}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="flex-end"
              alignItems="center"
              sx={{
                pointerEvents: { lg: "auto" },
                bgcolor: "background.default",
                borderRadius: 1,
                pl: 0.5
              }}
            >
              <Tooltip title="Previous row">
                <span>
                  <IconButton onClick={goToPreviousRow} disabled={!hasPreviousRow || isReviewRowLoading}>
                    <ArrowBackIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next row">
                <span>
                  <IconButton onClick={goToNextRow} disabled={!hasNextRow || isReviewRowLoading}>
                    <ArrowForwardIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {!isBulletinOpen && bulletinOptions.length > 0 && (
                <Button variant="outlined" onClick={() => setIsBulletinOpen(true)}>
                  Show PDF
                </Button>
              )}
            </Stack>
          </Stack>

          {isSelectedRowImported && selectedRow && (
            <Alert severity="success">
              This migration review row was already imported
              {hasLinkedTargetRecord ? ` to ${entityLabels[selectedRow.targetEntityType as BlacklistEntityType]} record ${selectedRow.targetRecordId}` : ""}
              . All fields and actions are locked.
            </Alert>
          )}
          {message && <Alert severity="success">{message}</Alert>}
          {advanceMessage && <Alert severity="info">{advanceMessage}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ position: "relative" }}>
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              sx={{
                pb: { lg: `${dashboardDesktopBottomPadding}px` },
                mb: { lg: `-${dashboardDesktopBottomPadding}px` }
              }}
            >
            <Grid
              item
              xs={12}
              lg={2}
              sx={{
                position: { lg: "sticky" },
                top: { lg: `${dashboardStickyPanelTop}px` },
                alignSelf: { lg: "flex-start" }
              }}
            >
              <Box>
                {selectedRow ? (
                  <SourceCsvChecklist
                    row={selectedRow}
                    fieldReview={fieldReviewByRow[selectedRow.id] || {}}
                    prefilledFields={prefilledFieldsByRow[selectedRow.id] || {}}
                    disabled={isSelectedRowImported}
                    hoveredReviewFieldId={hoveredReviewFieldId}
                    onCopy={handleCopyRawValue}
                    onAccept={(fieldId) => {
                      acceptSuggestedField(fieldId);
                      selectEvidenceForField(fieldId);
                    }}
                    onReject={(fieldId, value) => {
                      rejectSuggestedField(fieldId, value);
                      selectEvidenceForField(fieldId, formatDisplayValue(value));
                    }}
                    onNotApplicable={markFieldNotApplicable}
                    onClearReview={clearFieldReview}
                    onReviewFieldHover={setHoveredReviewFieldId}
                    onSelectFieldEvidence={selectEvidenceForField}
                  />
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, height: "100%" }}>
                    <Typography color="text.secondary">Select a row to see original CSV data.</Typography>
                  </Paper>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} lg={isBulletinOpen && bulletinOptions.length > 0 ? 5.5 : 10}>
              <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, minHeight: 720, height: "100%", borderRadius: 1.5 }}>
                {!selectedRow ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 420 }}>
                    <Typography color="text.secondary">Select a row to review.</Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1.75}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>Old row {selectedRow.sourceBlacklistId || selectedRow.id}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedRow.imported ? "Imported" : "Not imported"}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" noWrap={!selectedRowSummary.includes(" | ")}>
                          {selectedRowSummary || "No source summary"}
                        </Typography>
                      </Stack>
                      {selectedRow.manualReviewReason && (
                        <Box sx={{ maxWidth: { xs: "100%", md: "50%" }, textAlign: { xs: "left", md: "right" } }}>
                          <Typography variant="body2" color="text.secondary" sx={{ display: "block", lineHeight: 1.2, fontWeight: 600 }}>
                            Manual review reason
                          </Typography>
                          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                            {selectedRow.manualReviewReason}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {selectedRow.commitError && <Alert severity="error">{selectedRow.commitError}</Alert>}
                    {potentialMatchingRecordsPanel}
                    {isEditingExistingRecord && (
                      <Alert
                        severity="warning"
                        variant="filled"
                        sx={{
                          "& .MuiAlert-message": { width: "100%" }
                        }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                            Editing existing {entityLabels[selectedRow.targetEntityType as BlacklistEntityType]} record {selectedRow.targetRecordId}
                          </Typography>
                          <Typography variant="body2">
                            Save will update that existing blacklist record. This migration row is being reviewed against that specific linked record.
                          </Typography>
                        </Stack>
                      </Alert>
                    )}
                    {!isSelectedRowImported && !hasMatchedBulletin && (
                      <Alert severity="warning">
                        No bulletin URL matched this row. Import will create the blacklist record without bulletin links.
                      </Alert>
                    )}

                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      {...getReviewFieldHoverProps("targetEntityType")}
                      onClick={() => selectEvidenceForField("targetEntityType", selectedRow.targetEntityType ? entityLabels[selectedRow.targetEntityType] : "")}
                      sx={{ cursor: "pointer", ...(getReviewFieldHighlightSx("targetEntityType") as Record<string, unknown>) }}
                    >
                      {isEditingExistingRecord || isSelectedRowImported ? (
                        <Chip
                          color={isSelectedRowImported ? "info" : "warning"}
                          variant="filled"
                          label={isSelectedRowImported && hasLinkedTargetRecord
                            ? `Imported ${entityLabels[selectedRow.targetEntityType as BlacklistEntityType]} record ${selectedRow.targetRecordId}`
                            : `Editing ${entityLabels[selectedRow.targetEntityType as BlacklistEntityType]} record ${selectedRow.targetRecordId}`}
                          sx={{ fontWeight: 800 }}
                        />
                      ) : (
                        <>
                          <ToggleButtonGroup
                            exclusive
                            size="medium"
                            value={selectedRow.targetEntityType || null}
                            onChange={(_, value) => value && updateSelected({ targetEntityType: value })}
                            disabled={isSelectedRowImported}
                            sx={{ "& .MuiToggleButton-root": { px: 2.5, py: 1, fontWeight: 600 } }}
                          >
                            <ToggleButton value={BlacklistEntityType.Person}>Individual</ToggleButton>
                            <ToggleButton value={BlacklistEntityType.Company}>Entity</ToggleButton>
                            <ToggleButton value={BlacklistEntityType.Property}>Property</ToggleButton>
                          </ToggleButtonGroup>
                          {renderReviewActions("targetEntityType", selectedRow.targetEntityType ? entityLabels[selectedRow.targetEntityType] : "")}
                        </>
                      )}
                    </Stack>

                    <Stack
                      spacing={0.75}
                      {...getReviewFieldHoverProps("notes")}
                      sx={{ ...(getReviewFieldHighlightSx("notes", { wrapperOnly: true }) as Record<string, unknown>) }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Notes
                        </Typography>
                        {renderReviewActions("notes", selectedRow.notes)}
                      </Stack>
                      <TextField
                        value={selectedRow.notes || ""}
                        onChange={(event) => updateSelected({ notes: event.target.value })}
                        multiline
                        minRows={2}
                        disabled={isSelectedRowImported}
                      />
                    </Stack>

                    {(selectedRow.targetEntityType === BlacklistEntityType.Person || selectedRow.targetEntityType === BlacklistEntityType.Company) && (
                      <ContactInfo
                        individualInfo={individualInfo}
                        setIndividualInfo={changeIndividualInfo}
                        entityInfo={entityInfo}
                        changeEntityInfo={changeEntityInfo}
                        isIndividual={selectedRow.targetEntityType === BlacklistEntityType.Person}
                        setIsIndividual={setMigrationIsIndividual}
                        nameError={nameError}
                        companyError={companyError}
                        onBlurIndividual={() => {
                          if (cleanText(selectedRow.personLastName) || cleanText(selectedRow.taxId)) {
                            setNameError(false);
                          }
                        }}
                        onBlurEntity={() => {
                          if (cleanText(selectedRow.companyName) || cleanText(selectedRow.taxId)) {
                            setCompanyError(false);
                          }
                        }}
                        setBlacklistContactFromPhonebookContact={setBlacklistContactFromPhonebookContact}
                        hideIndividualEntityToggle={isEditingExistingRecord}
                        isEditMode={isEditingExistingRecord}
                        disabled={isSelectedRowImported}
                        renderReviewActions={renderReviewActions}
                        getReviewFieldHoverProps={getReviewFieldHoverProps}
                        getReviewFieldHighlightSx={getReviewFieldHighlightSx}
                      />
                    )}

                    {selectedRow.targetEntityType === BlacklistEntityType.Property && (
                      <NewPropertyInfo
                        propertyInfo={propertyInfo}
                        setPropertyInfo={setMigrationPropertyInfo}
                        county={selectedCounty}
                        setCounty={setMigrationCounty}
                        stateValue={selectedState || null}
                        setStateValue={setMigrationState}
                        stateError={propertyStateError}
                        cityError={propertyCityError}
                        addressError={propertyAddressError}
                        sblError={propertySblError}
                        isEditMode={isEditingExistingRecord}
                        renderReviewActions={renderReviewActions}
                        getReviewFieldHoverProps={getReviewFieldHoverProps}
                        getReviewFieldHighlightSx={getReviewFieldHighlightSx}
                        disabled={isSelectedRowImported}
                      />
                    )}

                    {otherSharedBulletinRecordsPanel}

                    <TextField
                      label="Review note"
                      value={selectedRow.reviewNote || ""}
                      onChange={(event) => updateSelected({ reviewNote: event.target.value })}
                      multiline
                      minRows={2}
                      disabled={isSelectedRowImported}
                    />

                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="flex-start">
                      {isSelectedRowImported ? (
                        <Button variant="contained" onClick={goToNextRow} disabled={!hasNextRow || isReviewRowLoading}>
                          {hasNextRow ? "Next row" : "No more rows"}
                        </Button>
                      ) : (
                        <>
                          <Stack spacing={0.25} alignItems="flex-end">
                            <Tooltip title={isEditingExistingRecord ? "This saves changes directly to the existing blacklist record loaded in the form." : ""}>
                              <span>
                                <Button variant="outlined" onClick={saveRow} disabled={isReviewRowLoading || isSaving}>
                                  {isEditingExistingRecord ? "Save existing record" : "Save draft"}
                                </Button>
                              </span>
                            </Tooltip>
                            {isEditingExistingRecord && (
                              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                                Updates record {selectedRow.targetRecordId}
                              </Typography>
                            )}
                          </Stack>
                          {!isEditingExistingRecord && (
                            <Button variant="contained" onClick={commitRow} disabled={isReviewRowLoading || isSaving || !selectedRow.targetEntityType}>
                              Import to blacklist
                            </Button>
                          )}
                        </>
                      )}
                    </Stack>
                  </Stack>
                )}
              </Paper>
            </Grid>

            {isBulletinOpen && bulletinOptions.length > 0 && (
              <Grid
                item
                xs={12}
                lg={4.5}
                sx={{
                  position: { lg: "sticky" },
                  top: { lg: `${dashboardStickyPanelTop}px` },
                  alignSelf: { lg: "flex-start" }
                }}
              >
                <Box
                  sx={{
                    height: { lg: `calc(100vh - ${dashboardStickyPanelTop + (dashboardStickyGutter * 2)}px)` },
                    pl: { lg: 0.5 }
                  }}
                >
                  <Stack spacing={1.5} sx={{ height: { lg: "100%" } }}>
                    {selectedRow && (
                      <BulletinMatchStrip
                        row={selectedRow}
                        disabled={isSelectedRowImported}
                        onOpen={(index) => openBulletin(selectedRow, index)}
                        onEdit={openEditBulletinModal}
                        onUnlink={(index) => void unlinkBulletin(index)}
                      />
                    )}
                    <BulletinViewer
                      options={bulletinOptions}
                      activeIndex={activeBulletinIndex}
                      activeField={activePdfField}
                      onActiveIndexChange={setActiveBulletinIndex}
                      isOpen={isBulletinOpen}
                      onClose={() => setIsBulletinOpen(false)}
                      onPdfLoadStart={handleBulletinPdfLoadStart}
                      onPdfLoadComplete={handleBulletinPdfLoadComplete}
                      onPdfLoadError={handleBulletinPdfLoadComplete}
                    />
                  </Stack>
                </Box>
              </Grid>
            )}
            </Grid>
            {isReviewRowLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 7,
                  minHeight: 420,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  pt: { xs: 12, lg: 18 },
                  bgcolor: "background.default",
                  pointerEvents: "all"
                }}
              >
                <Paper elevation={4} sx={{ px: 3, py: 2, borderRadius: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={24} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Loading review row
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Refreshing checklist, form, matches, and bulletin PDF...
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>
            )}
          </Box>
          <Snackbar
            open={Boolean(copyMessage)}
            autoHideDuration={1800}
            onClose={() => setCopyMessage("")}
            message={copyMessage}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          />
          <Dialog open={isPrefillModalOpen} onClose={() => !isPrefillLoading && setIsPrefillModalOpen(false)} fullWidth maxWidth="md">
            <DialogTitle>AI prefill suggestions</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  Review each suggested prefill from the old row data. Checked items will be applied to the current row.
                </Typography>

                {prefillError && <Alert severity="error">{prefillError}</Alert>}
                {prefillWarnings.map((warning) => (
                  <Alert key={warning} severity="warning">
                    {warning}
                  </Alert>
                ))}

                {isPrefillLoading && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                    <Typography>Asking Azure AI for suggested prefill values...</Typography>
                  </Paper>
                )}

                {!isPrefillLoading && !prefillSuggestions.length && !prefillError && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                    <Typography color="text.secondary">No suggestions to review for this row.</Typography>
                  </Paper>
                )}

                {!isPrefillLoading && prefillSuggestions.map((suggestion) => {
                  const isApproved = prefillDecisions[suggestion.key] !== "rejected";
                  return (
                    <Paper
                      key={suggestion.key}
                      variant="outlined"
                      onClick={() => {
                        if (suggestion.bulletinIndex !== undefined && suggestion.bulletinIndex !== null) {
                          setActiveBulletinIndex(suggestion.bulletinIndex);
                          setIsBulletinOpen(true);
                        }
                        setActivePdfField(toPdfFieldResult(suggestion));
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        cursor: "pointer",
                        borderColor: isApproved ? "success.light" : "error.light",
                        bgcolor: isApproved ? "success.lighter" : "error.lighter"
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "flex-start" }}>
                        <Checkbox
                          checked={isApproved}
                          onChange={(event) => setPrefillDecision(suggestion, event.target.checked ? "approved" : "rejected")}
                          sx={{ p: 0.25 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {suggestion.label || suggestion.key}
                            </Typography>
                            {suggestion.confidence && (
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                                {suggestion.confidence}
                              </Typography>
                            )}
                          </Stack>
                          <Typography variant="h6" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                            {suggestion.value}
                          </Typography>
                          {suggestion.reason && (
                            <Typography variant="body2" color="text.secondary">
                              {suggestion.reason}
                            </Typography>
                          )}
                          {suggestion.sourceText && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, overflowWrap: "anywhere" }}>
                              Source: {suggestion.sourceText}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" onClick={(event) => event.stopPropagation()}>
                          <Tooltip title="Approve this prefill">
                            <IconButton
                              aria-label={`approve AI prefill ${suggestion.key}`}
                              onClick={() => setPrefillDecision(suggestion, "approved")}
                              sx={{
                                color: "success.main",
                                border: "1px solid",
                                borderColor: isApproved ? "success.main" : "divider",
                                bgcolor: isApproved ? "success.lighter" : "background.paper"
                              }}
                            >
                              <CheckCircleOutlineIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject and copy value">
                            <IconButton
                              aria-label={`reject AI prefill ${suggestion.key}`}
                              onClick={() => setPrefillDecision(suggestion, "rejected")}
                              sx={{
                                color: "error.main",
                                border: "1px solid",
                                borderColor: !isApproved ? "error.main" : "divider",
                                bgcolor: !isApproved ? "error.lighter" : "background.paper"
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsPrefillModalOpen(false)} disabled={isPrefillLoading}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={applyPrefillSuggestions}
                disabled={isPrefillLoading || !prefillSuggestions.some((suggestion) => prefillDecisions[suggestion.key] !== "rejected")}
              >
                Apply approved
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={isBulletinEditOpen} onClose={() => !isSavingBulletinEdit && setIsBulletinEditOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Edit bulletin</DialogTitle>
            <DialogContent dividers>
              {isLoadingBulletinEdit ? (
                <Typography color="text.secondary">Loading bulletin...</Typography>
              ) : (
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                  <TextField
                    label="Bulletin number"
                    value={editingBulletin?.number || ""}
                    onChange={(event) => setEditingBulletin((current) => current ? { ...current, number: event.target.value } : current)}
                    fullWidth
                  />
                  <TextField
                    label="Bulletin date"
                    type="date"
                    value={editingBulletin?.date ? editingBulletin.date.slice(0, 10) : ""}
                    onChange={(event) => setEditingBulletin((current) => current ? { ...current, date: event.target.value || null } : current)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  {editingBulletin?.url && (
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      {editingBulletin.url}
                    </Typography>
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsBulletinEditOpen(false)} disabled={isSavingBulletinEdit}>
                Cancel
              </Button>
              <Button variant="contained" onClick={() => void saveBulletinEdit()} disabled={isLoadingBulletinEdit || isSavingBulletinEdit || !editingBulletin}>
                {isSavingBulletinEdit ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Container>
    </Page>
  );
}
